import { useQuery, type QueryClient } from "@tanstack/react-query";
import { mediumRepository } from "../repositories/mediumRepository";
import { terreiroRepository } from "../repositories/terreiroRepository";

export type InventoryListStatusFilter =
  | "IN_STOCK"
  | "LOW_STOCK"
  | "OUT_OF_STOCK";

export type InventoryListSortOrder = "asc" | "desc";

export interface InventoryListQueryFilters {
  name?: string;
  status?: InventoryListStatusFilter;
  sortOrder?: InventoryListSortOrder;
}

export const terreiroInventoryListQueryKey = (
  terreiroId: string,
  filters?: InventoryListQueryFilters,
) => ["terreiro", terreiroId, "inventory", filters ?? {}];

export const mediumInventoryListQueryKey = (
  mediumId: string,
  filters?: InventoryListQueryFilters,
) => ["medium", mediumId, "inventory", filters ?? {}];

export function invalidateInventoryStorageQueries(
  queryClient: QueryClient,
  terreiroId?: string,
  mediumId?: string,
) {
  const tasks: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: ["inventory-item"] }),
  ];

  if (terreiroId) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: ["terreiro", terreiroId, "inventory"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["terreiro", terreiroId, "pending-receipts"],
      }),
    );
  }

  if (mediumId) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: ["medium", mediumId, "inventory"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["medium", mediumId, "pending-receipts"],
      }),
    );
  }

  return Promise.all(tasks);
}

export const useInventoryListQuery = (
  terreiroId?: string,
  mediumId?: string,
  filters?: InventoryListQueryFilters,
) => {
  const useTerreiro = !!terreiroId;
  const useMedium = !terreiroId && !!mediumId;

  const queryKey = useTerreiro
    ? terreiroInventoryListQueryKey(terreiroId as string, filters)
    : useMedium
    ? mediumInventoryListQueryKey(mediumId as string, filters)
    : ["inventory", "none"];

  const queryFn = () => {
    if (useTerreiro) {
      return terreiroRepository.getTerreiroInventoryItems(
        terreiroId as string,
        filters,
      );
    }
    if (useMedium) {
      // Use mediumRepository for medium inventory queries
      return mediumRepository.getInventoryItemsByMedium(
        mediumId as string,
        filters,
      );
    }
    return Promise.resolve([]);
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: useTerreiro || useMedium,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
  });
};
